import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { BrowserAuthError } from '@azure/msal-browser';
import { login, loginWithMicrosoft } from '../api/auth';
import { useAuth } from '../auth/AuthContext';
import { loginRequest } from '../auth/msalConfig';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader } from '../components/ui/card';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { setTokens } = useAuth();
  const { instance } = useMsal();
  const navigate = useNavigate();

  useEffect(() => {
    instance
      .handleRedirectPromise()
      .then(async (result) => {
        if (!result?.idToken) return;
        try {
          const data = await loginWithMicrosoft(result.idToken);
          setTokens(data.accessToken, data.refreshToken);
          navigate('/flows');
        } catch {
          setError('Microsoft sign-in failed. Please try again.');
        }
      })
      .catch(() => {
        // No pending redirect / stale hash — safe to ignore.
      });
  }, [instance, setTokens, navigate]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const data = await login(email, password);
      setTokens(data.accessToken, data.refreshToken);
      navigate('/flows');
    } catch {
      setError('Invalid credentials.');
    } finally {
      setLoading(false);
    }
  }

  async function handleMicrosoftLogin() {
    setError(null);
    setLoading(true);
    try {
      await instance.loginRedirect(loginRequest);
    } catch (err: unknown) {
      if (err instanceof BrowserAuthError && err.errorCode === 'user_cancelled') {
        // no-op
      } else {
        setError('Microsoft sign-in failed. Please try again.');
      }
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/40 p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="items-center pb-2">
          <img src="/logo.svg" alt="FlowForge" className="h-12 w-12" />
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <p className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">
                {error}
              </p>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
              />
            </div>
            <Button type="submit" disabled={loading} className="w-full">
              {loading ? 'Logging in…' : 'Log in'}
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-card px-2 text-muted-foreground">or</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={handleMicrosoftLogin}
              disabled={loading}
              className="w-full"
            >
              <svg className="h-4 w-4" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
                <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
              </svg>
              Sign in with Microsoft
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              First run? <Link to="/setup" className="text-primary underline underline-offset-2">Setup</Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
